import {createContext, useContext, useEffect, useMemo, useRef} from "react";

type ShouldStop = boolean;

export interface PollingQueueEntry {
    fresh: boolean;
    cancelled: boolean;
    created: number; // milliseconds since epoch
    pollingRateMillis: number;
    fetchFactory: () => Promise<ShouldStop>;
}

type PollingQueueInput = Omit<PollingQueueEntry, "created" | "cancelled" | "fresh">;

type CancellationFunc = () => void;

interface IPollingQueue {
    enqueue(entry: PollingQueueInput): CancellationFunc;
}

export class PollingQueue {
    private _queue: PollingQueueEntry[] = [];
    private _queueProcessingFrequency: number;
    private _queueMaxConcurrency: number;
    private _queueCurrentConcurrency: number = 0;

    /**
     * Constructs a new PollingQueue with given configuration.
     *
     * @param queueProcessingFrequencyMillis How often the polling loop runs (default 1000ms).
     * @param queueMaxConcurrency Max number of concurrent entries (default 25).
     */
    constructor(queueProcessingFrequencyMillis = 1000, queueMaxConcurrency = 25) {
        this._queueProcessingFrequency = queueProcessingFrequencyMillis;
        this._queueMaxConcurrency = queueMaxConcurrency;

        this._pollingLoop();
    }

    /**
     * Add a polling entry to the queue.
     * @param entry Entry without created/cancelled fields.
     * @returns Cancellation function.
     */
    enqueue(entry: PollingQueueInput): CancellationFunc {
        const newEntry: PollingQueueEntry = {
            ...entry,
            fresh: true,
            cancelled: false,
            created: Date.now(),
        };
        this._queue.push(newEntry);
        return () => {
            newEntry.cancelled = true;
        };
    }

    private _requeue(entry: PollingQueueEntry): void {
        entry.created = Date.now();
        this._queue.push(entry);
    }

    private _pollingLoop(): void {
        for (
            let it = 0;
            it < this._queue.length && this._queueCurrentConcurrency < this._queueMaxConcurrency;
            it++
        ) {
            const entry = this._queue.shift();
            if (!entry) continue;

            this._queueCurrentConcurrency++;

            const entryCreationDelta = Date.now() - entry.created;
            const timeout = entry.fresh ? 0 : Math.max(entry.pollingRateMillis - entryCreationDelta, 0);

            setTimeout(() => {
                if (entry.cancelled) return;
                entry.fresh = false;

                entry
                    .fetchFactory()
                    .then(shouldStop => {
                        if (!shouldStop) {
                            this._requeue(entry);
                        }
                    })
                    .catch(shouldStopOrErr => {
                        if (shouldStopOrErr instanceof Error) {
                            return Promise.reject(shouldStopOrErr);
                        }

                        if (!shouldStopOrErr) {
                            this._requeue(entry);
                        }
                    })
                    .finally(() => {
                        this._queueCurrentConcurrency = Math.max(this._queueCurrentConcurrency - 1, 0);
                    });
            }, timeout);
        }

        setTimeout(() => {
            this._pollingLoop();
        }, this._queueProcessingFrequency);
    }
}

export const PollingQueueContext = createContext<PollingQueue>(new PollingQueue());

export function usePollingQueue(): IPollingQueue {
    return useContext(PollingQueueContext);
}

export function useBoundPollingQueue(): IPollingQueue {
    const origin = usePollingQueue();
    const boundEntries = useRef<CancellationFunc[]>([]);
    const queue = useMemo<IPollingQueue>(() => new class implements IPollingQueue {
        enqueue(entry: PollingQueueInput): CancellationFunc {
            const cancel = origin.enqueue(entry)
            boundEntries.current.push(cancel);
            return cancel;
        }
    }, []);
    useEffect(() => {
        return () => boundEntries.current.forEach(cancel => cancel());
    }, [])
    return queue;
}